import ApiService from "./ApiService";
import {blobToDataURL} from 'blob-util';
import JSZip from "jszip";

//The API service is a self-contained static provider to talk to the certblock api
const certblockAPI = new ApiService();

const UPLOAD_STATUS = {
	READY_FOR_UPLOAD: 'READY_FOR_UPLOAD',
	FINISHED_PROCESSING: 'FINISHED_PROCESSING',
	ERROR_PROCESSING: 'ERROR_PROCESSING'
};

/**
 * A document, that has been downloaded from the Certblock API.
 * @typedef DownloadedDocument
 * @property {string} filename The filename of the document. Usually generated by the download logic based on
 * @property {Blob} content The content of the file, that has been downloaded from the Certblock API
 */

/**
 * The DocumentService can be used to manage Documents via the certblock API.
 * It bundles complex upload procedures and download processes
 */
export default class DocumentService {

	/**
	 * Download a document in the browser.
	 * @param orderNumber
	 * @param deliveryNoteNumber
	 * @param orderPosition
	 * @param documentProfileId
	 * @param serialNumber
	 */
	async downloadDocument({orderNumber, deliveryNoteNumber, orderPosition, documentProfileId, serialNumber}) {
		let downloadedDocument = await this.fetchDocument({orderNumber, deliveryNoteNumber, orderPosition, documentProfileId, serialNumber});
		return this.downloadBlob(downloadedDocument);
	}

	/**
	 * Fetches a document from the API. It will fetch the necessary data including a presigned url from the API,
	 * download the file to the memory and return it as the correctly named file.
	 * @param orderNumber
	 * @param deliveryNoteNumber
	 * @param orderPosition
	 * @param documentProfileId
	 * @param serialNumber
	 * @return {Promise<DownloadedDocument>}
	 */
	async fetchDocument({orderNumber, deliveryNoteNumber, orderPosition, documentProfileId, serialNumber}) {
		console.debug("DEBUG[DocumentService.downloadDocument] triggered for:", {orderNumber, deliveryNoteNumber, orderPosition, documentProfileId, serialNumber});
		let apiParams = [orderNumber, deliveryNoteNumber, orderPosition, documentProfileId];
		if (serialNumber) {
			apiParams.push(serialNumber);
		}
		let downloadUrlApiResponse = await certblockAPI.callApi('documentProfile', apiParams, 'get');
		if (!downloadUrlApiResponse?.data?.downloadUrl) {
			console.error("ERROR[DocumentService.downloadDocument] requesting presigned URL failed for:", {orderNumber, deliveryNoteNumber, orderPosition, documentProfileId, serialNumber});
			throw new Error("Couldn't fetch download url from API");
		}
		console.debug("DEBUG[DocumentService.downloadDocument] received presigned download url:", downloadUrlApiResponse.data.downloadUrl);
		let presignedUrl = downloadUrlApiResponse.data.downloadUrl;
		const downloadResponse = await fetch(presignedUrl);
		if (!downloadResponse.ok) {
			console.error("ERROR[DocumentService.downloadDocument] downloading file failed for:", {orderNumber, deliveryNoteNumber, orderPosition, documentProfileId, serialNumber});
			throw new Error("Couldn't fetch file from Certblock");
		}
		let content = await downloadResponse.blob();
		let filename = `${orderNumber}_${deliveryNoteNumber}_${orderPosition}_${documentProfileId}${serialNumber ? "_" + serialNumber : ""}.pdf`;
		return {
			content,
			filename
		};
	}

	/**
	 * Upload a document to Certblock. The upload process uploads each selected file to its own upload slot, waits for
	 * the virus scan of every file to be completed and then assigns all scanned slots at once. The backend merges the
	 * scanned slots into a single document (in the order the slots are supplied) and stores it at the target location.
	 * If any file fails the virus scan, the whole upload is aborted and nothing is assigned.
	 * @param orderNumber
	 * @param deliveryNoteNumber
	 * @param orderPosition
	 * @param documentProfileId
	 * @param serialNumber
	 * @param {Array<File>} files
	 * @return {Promise<void>}
	 */
	async uploadDocument({orderNumber, deliveryNoteNumber, orderPosition, documentProfileId, serialNumber, files}) {
		let fileList = Array.from(files || []);
		if (fileList.length < 1) {
			throw new Error("Empty file list to upload");
		}

		// Upload every file to its own slot and wait for the scan. Keep the slot ids in the order of the selected
		// files, so the backend merges the resulting document in the expected page order.
		let slotIds = await Promise.all(fileList.map(file => this.uploadFileToSlot({
			orderNumber,
			deliveryNoteNumber,
			orderPosition,
			documentProfileId,
			file
		})));

		// Now all uploads and scans were successful and the documents can be stored (and merged) inside Certblock
		let apiParams = {slotIds};
		if (serialNumber) {
			apiParams.serialNumber = serialNumber;
		}
		let objectStoreResult = await certblockAPI.callApi(
			'profileDocsAssignment',
			[orderNumber, deliveryNoteNumber, orderPosition, documentProfileId],
			'put',
			apiParams
		);
	}

	/**
	 * Uploads a single file to a freshly requested upload slot and waits until the virus scan finished.
	 * @param orderNumber
	 * @param deliveryNoteNumber
	 * @param orderPosition
	 * @param documentProfileId
	 * @param {File} file
	 * @return {Promise<string>} The id of the slot the file has been uploaded to
	 */
	async uploadFileToSlot({orderNumber, deliveryNoteNumber, orderPosition, documentProfileId, file}) {
		// First receive the slot id and the upload URL from Certblock
		console.debug("DEBUG[DocumentService.uploadFileToSlot] Requesting upload slot for (orderNumber, deliveryNoteNumber, orderPosition, documentProfileId)", orderNumber, deliveryNoteNumber, orderPosition, documentProfileId);
		let slotResponse = await certblockAPI.callApi(
			'setUploadSlots',
			[orderNumber, deliveryNoteNumber, orderPosition],
			'post',
			{'profileId': documentProfileId},
		)
		let slotId = slotResponse.data.slotId;
		let presignedUrl = slotResponse.data.presignedUrl;
		console.debug("DEBUG[DocumentService.uploadFileToSlot] Got a slot id:", slotId);

		// Now upload the file to the upload slot
		let uploadResponse = await certblockAPI.callApi(
			'uploadFile',
			[],
			'put',
			'',
			{},
			presignedUrl,
			file
		);
		//TODO: error handling

		// Now wait until the uploaded document has been scanned
		let uploadStatus = slotResponse.data.status;
		while ([UPLOAD_STATUS.ERROR_PROCESSING, UPLOAD_STATUS.FINISHED_PROCESSING].indexOf(uploadStatus) < 0) {
			console.debug("DEBUG[DocumentService.uploadFileToSlot] Poll for upload slot status");
			await (new Promise((resolve, reject) => setTimeout(resolve, 1000)));
			let uploadStatusPollResponse = await certblockAPI.callApi(
				'pollingFiles',
				[orderNumber, deliveryNoteNumber, orderPosition, slotId],
				'get',
			);
			uploadStatus = uploadStatusPollResponse.data.status;
		}

		if (uploadStatus !== UPLOAD_STATUS.FINISHED_PROCESSING) {
			throw new Error("Error uploading file");
		}

		return slotId;
	}

	async deleteDocument({orderNumber, deliveryNoteNumber, orderPosition, documentProfileId, serialNumber, files}) {
		let apiParams = [orderNumber, deliveryNoteNumber, orderPosition, documentProfileId];
		if (serialNumber) {
			apiParams.push(serialNumber);
		}
	}

	/**
	 * Will retrieve a set of orderPositions, that documents have been uploaded for. Will reduce the resultset
	 * to unique orderPositions. e.g. multiple results with different serial numbers, but the same orderposition will be merged.
	 * @param orderNumber
	 * @param deliveryNoteNumber
	 * @return {Promise<Array<String>>}
	 */
	async getOrderPositionsWithDocumentsOfDeliveryNote({orderNumber, deliveryNoteNumber}) {
		console.log("delivery", deliveryNoteNumber)
		/** @type Array<OrderPositionUpload> */
		let orderPositions = (await certblockAPI.callApi(
			'getOrderPositions',
			[orderNumber, deliveryNoteNumber],
			'get'
		)).data;
		return [...new Set(orderPositions.map(o => o.orderPosition))];
	}

	/**
	 * @typedef DocumentProfile
	 * @property {String} id The if of the document profile
	 * @property {String} name The localized name of a document profile
	 */

	/**
	 * Fetch all relevant document profiles for an orderposition including the translation.
	 * @param orderNumber
	 * @param deliveryNoteNumber
	 * @param orderPosition
	 * @param language
	 * @return {Promise<Array<DocumentProfile>>}
	 */
	async getConfiguredDocumentProfilesOfOrderPosition({orderNumber, deliveryNoteNumber, orderPosition, language = "de"}) {
		let documentProfileIdList = (await certblockAPI.callApi(
			'documentProfilesAll',
			[orderNumber, deliveryNoteNumber, orderPosition],
			'get'
		)).data;
		// merge with manually created certificate data
		let allUploadedDocuments = await this.getAllDocumentsOfOrderPosition({orderNumber, deliveryNoteNumber, orderPosition});
		let allUploadedDocumentProfiles = allUploadedDocuments.reduce((acc, curr) => {
			acc.push(...curr.documents);
			return acc;
		}, []);
		documentProfileIdList = [...new Set([...documentProfileIdList, ...allUploadedDocumentProfiles])];
		return Promise.all(documentProfileIdList.map(async documentProfileId => {
			let profileName = (await certblockAPI.callApi(
				'getProfileName',
				[orderNumber, deliveryNoteNumber, orderPosition, documentProfileId, language],
				'get',
			)).data
			return {
				id: documentProfileId,
				name: profileName
			};
		}))
	}

	/**
	 * @typedef OrderPositionUpload
	 * @property {String} orderPosition
	 * @property {String} serialNumber
	 */

	/**
	 *
	 * @param orderNumber
	 * @param deliveryNoteNumber
	 * @param orderPosition
	 * @return {Promise<void>}
	 */
	async getAllDocumentsOfOrderPosition({orderNumber, deliveryNoteNumber, orderPosition}) {
		/** @type OrderPositionUpload */
		let availableOrderPositionUploads = (await certblockAPI.callApi(
			'getOrderPositions',
			[orderNumber, deliveryNoteNumber],
			'get',
		)).data;
		console.log("available", availableOrderPositionUploads, orderPosition);
		let relevantOrderPositionUploads = availableOrderPositionUploads.filter(o => o.orderPosition === orderPosition);
		let res = await Promise.all(relevantOrderPositionUploads.map(async orderPositionUpload => {
			console.log(JSON.stringify(orderPositionUpload));
			if(orderPositionUpload.serialNumber) {
				console.log("serial number upload")
				let apiParameters = [orderNumber, deliveryNoteNumber, orderPosition, orderPositionUpload.serialNumber];
				let uploadedDocumentProfileIdList = (await certblockAPI.callApi(
					'documentProfiles',
					apiParameters,
					'get'
				)).data;
				return Object.assign({}, orderPositionUpload, {documents: uploadedDocumentProfileIdList});
			}
			let documentProfileIdList = (await certblockAPI.callApi(
				'documentProfiles',
				[orderNumber, deliveryNoteNumber, orderPosition],
				'get'
			)).data;
			return Object.assign({}, orderPositionUpload, {documents: documentProfileIdList});
		}));
		console.log(res);
		return res;
	}

	/**
	 * Will package the supplied documents into a zip file
	 * @param {Array<DownloadedDocument>} downloadedDocuments
	 * @return {Promise<Blob>}
	 */
	async packageDocuments({downloadedDocuments}) {
		let zip = new JSZip();
		for(let document of downloadedDocuments) {
			zip.file(document.filename, document.content);
		}
		return zip.generateAsync({type: "blob"});
	}

	/**
	 * Download all documents uploaded for a specific order- and delivery node number
	 * @param orderNumber
	 * @param orderPosition
	 * @param deliveryNoteNumber
	 * @return {Promise<void>}
	 */
	async downloadAllDocuments({orderNumber, orderPosition, deliveryNoteNumber}) {
		let availableDocuments = await this.getAllDocumentsOfOrderPosition({orderNumber, orderPosition, deliveryNoteNumber});
		console.log("available documents for download", availableDocuments, orderNumber, orderPosition, deliveryNoteNumber);
		let downloadedDocuments = [];
		let documentDownloader = [];
		for(let documentDefinition of availableDocuments) {
			for(let documentProfileId of documentDefinition.documents) {
				documentDownloader.push(this.fetchDocument({
					orderNumber,
					orderPosition,
					deliveryNoteNumber,
					documentProfileId,
					serialNumber: documentDefinition.serialNumber
				}));
			}
		}
		downloadedDocuments = await Promise.all(documentDownloader);
		let documentZipFile = await this.packageDocuments({downloadedDocuments});
		return this.downloadBlob({
			filename: `${orderNumber}_${deliveryNoteNumber}_${orderPosition}.zip`,
			content: documentZipFile
		});
	}

	/**
	 * Triggers the local download of a file from memory (in blob format)
	 * @param filename
	 * @param content
	 * @return {Promise<void>}
	 */
	async downloadBlob({filename, content}) {
		const dataUrl = await blobToDataURL(content);
		const downloadLink = document.createElement('a');

		downloadLink.href = dataUrl;
		downloadLink.download = filename;
		downloadLink.click();
	}

}